import { conf } from "lib/conf";
import queryString from "query-string";
import "whatwg-fetch";
import { NetworkError, ParseError, ServerError } from "./errors";

const parseErrorBody = (body) => {
  const { errors } = body; // ApiV3-formatted errors array
  const message = JSON.stringify(errors, null, "  "); // pretty-printed error details
  return ServerError(message, { details: errors });
};

const rejectBasedOnHTTPStatus = (response) => {
  if (response.ok) {
    return response;
  } else {
    return response
      .json()
      .catch((err) => Promise.reject(ParseError.cast(err)))
      .then((json) => Promise.reject(parseErrorBody(json)));
  }
};

const defaultOpts = {
  retries: 1, // number of times to retry the request in the event of a network error
};

const addQueryString = (path, params) => {
  if (params && Object.entries(params).length > 0) {
    return path + "?" + queryString.stringify(params);
  } else {
    return path;
  }
};

const req =
  (method) =>
  (path, opts = defaultOpts) => {
    // the default headers are listed first so that
    // they can be overridden manually, but this is not recommended
    const headers = {
      "x-rwgps-api-key": conf.RWGPS_API_KEY,
      "x-rwgps-api-version": 3,
      "content-type": "application/json",
      accept: "application/json",
      ...opts.headers,
    };
    // the content-type header needs to not be present in order to get the browser
    // to generate a multipart/form-data header with a valid boundary specified.
    if (headers["content-type"] === undefined) {
      delete headers["content-type"];
    }
    const { body, retries, params } = opts;
    const url = addQueryString(path, params);
    const makeRequest = (retriesRemaining) => {
      const r = window.fetch(url, {
        credentials: "same-origin",
        method,
        headers,
        body,
      });
      // fetch rejections means network error, so retry if we
      // get a rejection at this stage
      return retriesRemaining > 0 ? r.catch((e) => makeRequest(retriesRemaining - 1)) : r;
    };

    return makeRequest(retries)
      .catch((e) => Promise.reject(NetworkError.cast(e)))
      .then(rejectBasedOnHTTPStatus)
      .then((res) => res.json().catch((e) => Promise.reject(ParseError.cast(e))));
  };

// ApiV3 base request object. Replaces fetchJSON.
// usage example:
//
// request.get('/endpoint', {retries: 2})
//
// it will RESOLVE with the JSON sent by the server
// it will REJECT with one of the following errors
// - NetworkError if it encountered network issues and no retries were successful
// - ParseError to indicate that the server returned a malformed JSON response
// - ServerError to indicate that the server responded with an errors[] array
export const request = {
  get: req("GET"),
  post: req("POST"),
  patch: req("PATCH"),
  delete: req("DELETE"),
};
